import { Action } from '@ngrx/store';
import { Option } from '@sapphire/result';
import {
  type AdvancedQualification,
  buildLocaleChangeTargeting,
  type Qualification,
  type Quota,
  type SurveyLocaleData,
  type SurveyMetadataForm,
  type SurveyQualification,
  buildQuotaAllocationBasis,
  type LocaleChangeTargeting,
  mapSurveyLocalization,
  type QualificationsRecord,
  type Survey,
  SurveyInfo,
  SurveyKind,
  SurveyStatusInfo,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  CachedEntry,
  surveyAdvancedQualificationCacheKey,
} from '../../../utils';
import {
  SurveyCreationReferenceActions,
  type SurveyCreationReferenceApiActions,
} from '../../reference/survey-creation-reference.actions';
import type { SurveyCreationAudienceActions } from '../survey-creation-audience.actions';

// Unfiltered query: every advanced question for a locale.
export const ALL_QUESTIONS_SEARCH = '';
export const ALL_QUESTIONS_CATEGORY_IDS: number[] = [];

// Resolved once at dispatch, before the active survey can move.
export interface LocaleChangeRequest {
  readonly locale: SurveyLocaleData;
  readonly localeKey: string;
  readonly questionsKey: string;
  readonly survey: Survey;
  readonly qualifications: readonly SurveyQualification[];
  readonly quotas: readonly Quota[];
  readonly needsQuestions: boolean;
  readonly deleteCurrent: boolean;
  readonly totalAllocation: number;
}

export interface LocaleChangeRequestSources {
  readonly survey: Survey;
  readonly metadata: Option<SurveyMetadataForm>;
  readonly qualifications: readonly SurveyQualification[];
  readonly quotas: readonly Quota[];
}

export interface LocaleCatalogs {
  readonly catalogRecord: QualificationsRecord;
  readonly nextCatalog: readonly Qualification[];
  readonly nextQuestions: readonly AdvancedQualification[];
}

export type ChangeCreatedSurveyLocaleAction = ReturnType<
  typeof SurveyCreationAudienceActions.changeCreatedSurveyLocale
>;

export type CatalogLoadFailureAction =
  | ReturnType<
      typeof SurveyCreationReferenceApiActions.loadQualificationsFailure
    >
  | ReturnType<typeof SurveyCreationReferenceApiActions.loadQuestionsFailure>;

// Only a created (has an id), standard, still-draft survey can change locale this way.
export function eligibleSurveyForLocaleChange(
  survey: Nullable<Survey>,
  surveyId: Nullable<number>,
): Option<Survey> {
  return Option.from(survey)
    .filter(() => !!surveyId)
    .filter((currentSurvey) => SurveyKind.isStandard(currentSurvey))
    .filter((currentSurvey) => SurveyStatusInfo.isDraft(currentSurvey));
}

export function toLocaleChangeRequest(
  { locale, deleteCurrent }: ChangeCreatedSurveyLocaleAction,
  { survey, metadata, qualifications, quotas }: LocaleChangeRequestSources,
): LocaleChangeRequest {
  const localeKey = mapSurveyLocalization(
    locale.countryCode,
    locale.languageShortCode,
  );
  return {
    locale,
    localeKey,
    questionsKey: surveyAdvancedQualificationCacheKey(
      ALL_QUESTIONS_SEARCH,
      ALL_QUESTIONS_CATEGORY_IDS,
      localeKey,
    ),
    survey,
    qualifications,
    quotas,
    needsQuestions: new SurveyInfo(survey).hasAdvancedQualifications(),
    deleteCurrent,
    totalAllocation: metadata
      .andThen((form) => Option.from(buildQuotaAllocationBasis(form).value))
      .unwrapOr(0),
  };
}

export function localeCatalogLoads(request: LocaleChangeRequest): Action[] {
  return [
    SurveyCreationReferenceActions.loadQualifications(request.locale),
    ...Option.some(request)
      .filter(({ needsQuestions }) => needsQuestions)
      .mapOr([], ({ localeKey }) => [
        SurveyCreationReferenceActions.loadQuestions({
          search: ALL_QUESTIONS_SEARCH,
          categoryIds: ALL_QUESTIONS_CATEGORY_IDS,
          locale: localeKey,
        }),
      ]),
  ];
}

export function loadedLocaleCatalogs(
  request: LocaleChangeRequest,
  catalogRecord: QualificationsRecord,
  questionsRecord: Record<string, CachedEntry<AdvancedQualification[]>>,
): Option<LocaleCatalogs> {
  const nextQuestions = Option.from(questionsRecord[request.questionsKey]);
  return Option.from(catalogRecord[request.localeKey])
    .filter((nextCatalog) => nextCatalog.length > 0)
    .filter(() => !request.needsQuestions || nextQuestions.isSome())
    .map((nextCatalog) => ({
      catalogRecord,
      nextCatalog,
      nextQuestions: nextQuestions.mapOr([], (entry) => entry.data),
    }));
}

export function isLocaleCatalogLoadFailure(
  request: LocaleChangeRequest,
  failure: CatalogLoadFailureAction,
): boolean {
  return 'key' in failure
    ? failure.key === request.questionsKey
    : mapSurveyLocalization(failure.countryCode, failure.languageShortCode) ===
        request.localeKey;
}

export function localeChangeTargetingFor(
  request: LocaleChangeRequest,
  { catalogRecord, nextCatalog, nextQuestions }: LocaleCatalogs,
): LocaleChangeTargeting {
  return buildLocaleChangeTargeting({
    previousCatalog: Option.from(
      catalogRecord[
        mapSurveyLocalization(
          request.survey.countryCode,
          request.survey.languageShortCode,
        )
      ],
    ).unwrapOr([]),
    nextCatalog,
    nextAdvancedQualificationCodes: nextQuestions.map((question) =>
      Number(question.attributeId),
    ),
    surveyQualifications: request.qualifications,
    quotas: request.quotas,
    totalAllocation: request.totalAllocation,
  });
}

// Action props are plain-null (serializable).
export function replacedSurveyId({
  survey,
  deleteCurrent,
}: LocaleChangeRequest): Nullable<number> {
  return deleteCurrent ? survey.id : null;
}
